import { useMemo, useCallback, useEffect, useRef, useState } from "react";
import CandlestickChartIcon from "assets/svg/candlestick-chart-icon.svg";
import LineChartIcon from "assets/svg/line-chart-icon.svg";
import "bootstrap-daterangepicker/daterangepicker.css";
import ContentWrapper from "components/content-wrapper/ContentWrapper";
import ChartTimeSelectionDropdown from "components/dashboard/ChartTimeSelectionDropdown";
import { CustomRangeDetails } from "components/dashboard/CustomRangeDetails";
import RefreshRateDropdown from "components/dashboard/RefreshRateDropdown";
import {
    DEFAULT_OPTION,
    DEFAULT_ZOOM_OPTION,
    HttpStatus,
    Operator,
    PANEL_TOKEN_ZERO_TOKEN,
    UnknownDevice,
    DATE_TIME_FORMATS,
} from "constant";
import { ChartSource } from "constant/chart";
import { TooltipComponentOption } from "echarts";
import ReactECharts, { EChartsOption } from "echarts-for-react";
import useChartRefreshRate from "hooks/useChartRefreshRate";
import useChartWidth from "hooks/useChartWidth";
import useSelectChartTimeRange from "hooks/useSelectChartTimeRange";
import { cloneDeep, get, isEmpty, set, startCase } from "lodash";
import moment from "moment";
import { getDeviceConfigById } from "service/gatewayService";
import LocalStorageService from "service/localStorageService";
import { getSampleQuery } from "service/proqueryService";
import { BasicSensorChartProps, ChartState } from "types/Charts";

import {
    addDecimalPlace,
    convertPrometheusTimeToSeconds,
    formattedChartValue,
    formatValueWithUnit,
    getAccuracyFromOBJ,
    getChartMinOrMax,
    getYaxisDetails,
    isHttpSuccess,
    isIntegerChart,
    monthsToSeconds,
    parseColorToHex,
    totalDoesNotExceedMax,
} from "utils/functions";
import { getMode } from "./ActuatorChart";
import WidgetBox from "../WidgetBox";
import SensorListTooltip from "./components/SensorListTooltip";
import { useDateTimeFormatStore } from "hooks/useDateTimeFormatStore";
import { getCommonXAxis } from "utils/chart";
import { metricStore } from "hooks/useMetricDefined";
import { Alert } from "types";
import { APP_CONFIG } from "config/appConfig";
import Avatar from "@mui/material/Avatar";

// ----------------- http status -----------------
const { FORBIDDEN } = HttpStatus;

const convertLegacyQuery = (query: string): string => {
    if (!query.startsWith("/")) {
        return query;
    }

    const regex = /^\/(\d+)\/(\d+)\/([^{]+)(\{.+\})$/;
    const match = regex.exec(query);
    if (!match) {
        return query;
    }

    const [, cls, appId, unit, labels] = match;
    const properMetric = metricStore.getState().getMQTTQuery(cls, appId, unit);

    if (properMetric) {
        return `${properMetric}${labels}`;
    }

    return query;
};

const sensorChartNoDataStyleOption = (
    sensorType: string,
    sensorUnit: string,
    chartSource: ChartSource,
    longDateTimeFormat: string
) => {
    let subTitle = sensorType;
    if (chartSource === ChartSource.MULTIPLE_SENSORS) {
        subTitle += ` - combined data`;
    }

    const sensorUnitLine = `- ${sensorUnit}`;

    return [
        {
            path: "title.subtext",
            value: `{s6|${subTitle}}\n{s2|${sensorUnitLine}} {s5|    Min/Max} {s3| ${formatValueWithUnit(
                "-/-",
                sensorUnit || ""
            )}}\n{s1|${moment().format(longDateTimeFormat)}}`,
        },
    ];
};

const actuatorChartNoDataStyleOption = (
    sensorType: string,
    sensorUnit: string,
    sensorConfigRes: any,
    longDateTimeFormat: string
) => [
        {
            path: "title.subtext",
            value: `{s6|${startCase(sensorType)}}\n{s3|Mode: }{s5|${getMode({
                sensorConfigRes,
                sensorType,
            })}}\n{s2|${formatValueWithUnit(
                "-",
                sensorUnit || ""
            )}}{s1|    ${moment().format(longDateTimeFormat)}}`,
        },
    ];

const getChartAreaAlertConfig = ({
    vars_,
    ops,
    sensorUnit,
    formatChartValue,
}: {
    vars_: any;
    ops: any;
    sensorUnit: string;
    formatChartValue: (value: any) => string;
}) => {
    const thresholdLine: any = [];
    const thresholdArea: any = [];
    let position = "";

    const primaryAlertThreshold = vars_[1];
    const secondaryAlertThreshold = vars_[3];

    if (ops.length === 1) {
        // ops.length === 1 means single alert value

        position =
            ops[0] === Operator.LESS_THAN
                ? "insideStartTop"
                : "insideStartBottom";
        thresholdArea.push([
            {
                name: `${primaryAlertThreshold}`,
                yAxis: `${primaryAlertThreshold}`,
            },
            {
                yAxis:
                    ops[0] === Operator.LESS_THAN
                        ? `${Number.MIN_SAFE_INTEGER}`
                        : `${Number.MAX_SAFE_INTEGER}`,
            },
        ]);

        thresholdLine.push({
            yAxis: Number(primaryAlertThreshold),
            label: {
                show: true,
                position,
                color: "#E86881",
                formatter: (params: any) =>
                    `${formatChartValue(params.value)} ${sensorUnit}`,
            },
        });
    } else {
        // ops.length !== 1 means dual alert value

        if (ops.toString() === Operator.WITHIN_RANGE.toString()) {
            // and - within range
            thresholdArea.push([
                {
                    yAxis: `${primaryAlertThreshold}`,
                },
                {
                    yAxis: `${secondaryAlertThreshold}`,
                },
            ]);
        } else if (ops.toString() === Operator.OUT_OF_RANGE.toString()) {
            // or - out of range
            thresholdArea.push(
                [
                    {
                        yAxis: `${primaryAlertThreshold}`,
                    },
                    {
                        yAxis: `${Number.MIN_SAFE_INTEGER}`,
                    },
                ],
                [
                    {
                        yAxis: `${secondaryAlertThreshold}`,
                    },
                    {
                        yAxis: `${Number.MAX_SAFE_INTEGER}`,
                    },
                ]
            );
        }
        thresholdLine.push(
            {
                yAxis: Number(primaryAlertThreshold),
                label: {
                    show: true,
                    position: "insideStartBottom",
                    color: "#E86881",
                    formatter: (params: any) =>
                        `${formatChartValue(params.value)} ${sensorUnit}`,
                },
            },
            {
                yAxis: Number(secondaryAlertThreshold),
                label: {
                    show: true,
                    position: "insideStartTop",
                    color: "#E86881",
                    formatter: (params: any) =>
                        `${formatChartValue(params.value)} ${sensorUnit}`,
                },
            }
        );
    }

    return { thresholdLine, thresholdArea };
};

const chartAlertAreaOptionConfig = ({
    thresholdLine,
    thresholdArea,
    accuracy,
}: any) => ({
    markLine: {
        symbol: "none",
        data: thresholdLine,
        animationDuration: 0,
        lineStyle: {
            color: "#E86881",
            width: 2,
            type: "solid",
        },
        silent: true,
        precision: accuracy,
    },
    markArea: {
        itemStyle: {
            color: "rgba(255, 173, 177, 0.2)",
        },

        label: {
            show: false,
        },
        silent: true,
        data: thresholdArea,
    },
    smooth: true,
    type: "line",
    data: [],
});
const candlestickChartAlertAreaOptionConfig = ({
    thresholdLine,
    thresholdArea,
    accuracy,
}: any) => ({
    markLine: {
        label: {
            show: false,
        },
        animationDuration: 0,
        symbol: "none",
        data: thresholdLine,
        lineStyle: {
            color: "#E86881",
            width: 2,
            type: "solid",
        },
        silent: true,
        precision: accuracy,
    },
    markArea: {
        itemStyle: {
            color: "rgba(255, 173, 177, 0.05)",
        },

        label: {
            show: false,
        },
        silent: true,
        data: thresholdArea,
    },
});

const getMinMaxFromArray = ({
    array,
    accuracy,
}: {
    array: any;
    accuracy: any;
}) => {
    if (!array.length) return { min: Number.NaN, max: Number.NaN };

    const minValue = Number(array.sort((a: any, b: any) => a[1] - b[1])[0][1]);
    const maxValue = Number(array.sort((a: any, b: any) => b[1] - a[1])[0][1]);

    return {
        min: Number.parseFloat(addDecimalPlace(minValue, accuracy)),
        max: Number.parseFloat(addDecimalPlace(maxValue, accuracy)),
    };
};

const yAxisMinMaxChartOptions = ({ min, max }: { min: any; max: any }) => [
    {
        path: "yAxis.max",
        value: max,
    },
    {
        path: "yAxis.min",
        value: min,
    },
];

interface TooltipData {
    isCandlestickChart: boolean;
    sensorName: string;
    sensorUnit: string;
    formatChartValue: (value: any) => string;
}

const createTooltipComponentOptions = ({
    isCandlestickChart,
    sensorName,
    sensorUnit,
    formatChartValue,
    longDateTimeFormat,
}: TooltipData & { longDateTimeFormat: string }): TooltipComponentOption => {
    const createCurrentValueDiv = (
        currentValue: string,
        markerColor: string,
        seriesName: string
    ): HTMLDivElement => {
        const d = document.createElement("div");
        d.className = "mt-1 mb-1";

        const marker = document.createElement("span");
        marker.style.display = "inline-block";
        marker.style.marginRight = "4px";
        marker.style.borderRadius = "10px";
        marker.style.width = "10px";
        marker.style.height = "10px";
        marker.style.backgroundColor = markerColor;
        d.appendChild(marker);

        const seriesLabel = isCandlestickChart ? sensorName : seriesName;
        d.appendChild(
            document.createTextNode(`${seriesLabel}\u00A0\u00A0\u00A0`)
        );

        const b = document.createElement("b");
        b.innerText = formatValueWithUnit(currentValue, sensorUnit);
        d.appendChild(b);
        d.appendChild(document.createElement("br"));

        return d;
    };

    const formatter = (params: any): HTMLElement => {
        const date = params[0].value[0];

        const el = document.createElement("div");

        const dateTimeText = document.createTextNode(
            moment(date, "x").format(longDateTimeFormat)
        );
        el.appendChild(dateTimeText);
        el.appendChild(document.createElement("br"));
        el.appendChild(
            createCurrentValueDiv(
                formatChartValue(params[0].value[1]),
                params[0].color,
                params[0].seriesName
            )
        );

        if (isCandlestickChart) {
            const minValue = formatChartValue(params[2]?.value?.[1]);
            const maxValue = formatChartValue(params[1]?.value?.[1]);
            const minMaxText = document.createTextNode(
                `Min ${formatValueWithUnit(
                    minValue,
                    sensorUnit
                )}\u00A0\u00A0\u00A0Max ${formatValueWithUnit(
                    maxValue,
                    sensorUnit
                )}`
            );
            el.appendChild(minMaxText);
            el.appendChild(document.createElement("br"));
        }

        return el;
    };

    return {
        trigger: "axis",
        renderMode: "html",
        appendTo: "body",
        formatter,
    };
};

const DEFAULT_ACCURACY: string = "1";

const updateSeriesPayloadWithNameAndDecimal = ({
    series,
    accuracy,
    sensorName,
}: {
    series: any;
    accuracy: string;
    sensorName: string;
}) => {
    series.forEach((element: { name: string; data: [number, string][] }) => {
        element.name = sensorName;
        element.data = element.data.map(
            ([timestamp, value]: [number, string]) => [
                timestamp,
                addDecimalPlace(value, accuracy),
            ]
        );
    });
};

// Use this function composition to format the value based on the accuracy
const createFormattedChartValue =
    (accuracy: number, useExponential: boolean = true) =>
        (value: any) => {
            return formattedChartValue({
                sensorAccuracy: accuracy,
                value,
                useExponential,
            });
        };

/**
 * Extracts the latest data point from chart series and formats it for display
 * @param series - Array of chart series containing data points
 * @param accuracy - Number of decimal places to show
 * @param timeFormat - Time format string for moment.js
 * @returns Formatted live data with date and time
 * liveData: latest data (or) "-" if no data
 * liveDate: latest data date (or) current date
 * liveTime: latest data time (or) current time
 */
const getLiveDataInfo = ({
    series,
    accuracy,
    timeFormat,
}: {
    series: any;
    accuracy: string;
    timeFormat: string;
}): { liveData: string; liveDate: string; liveTime: string } => {
    const now = moment().unix() * 1000;

    const lastSeriesIndex = series.length - 1;
    const seriesDataPath = `[${lastSeriesIndex}].data`;
    const lastDataIndex = get(series, seriesDataPath, []).length - 1;
    const pathToLatestData = `[${lastSeriesIndex}].data[${lastDataIndex}]`;

    let liveData: string = "-";
    const latestData = get(series, `${pathToLatestData}[1]`, "-");

    if (latestData !== "-") {
        liveData = addDecimalPlace(latestData, accuracy);
    }

    const unix_timestamp = get(series, `${pathToLatestData}[0]`, now);
    const timestampMoment = moment(unix_timestamp);

    // Format date and time consistently
    const liveDate = timestampMoment.format(DATE_TIME_FORMATS.DATE_ONLY);
    const liveTime = timestampMoment.format(timeFormat);

    return { liveData, liveDate, liveTime };
};

const shouldShowAggregation = (
    selected_time_value: number,
    sensorReportRate: number
) => {
    const totalSample = Math.floor(selected_time_value / sensorReportRate);
    return (
        selected_time_value > monthsToSeconds(6) ||
        !totalDoesNotExceedMax({ total: totalSample, numberOfChart: 1 })
    );
};

const addNoteToChart = (
    isActuator: boolean,
    isBinary: boolean,
    isAggregated: boolean,
    option: EChartsOption,
    boxWidth: number
): EChartsOption => {
    const baseTitle = Array.isArray(option.title)
        ? { ...option.title[0] }
        : { ...option.title };

    const isShowNote = (isBinary || isActuator) && isAggregated;

    const secondTitle = {
        text: "Averaged data may not reflect actual states. Use a shorter time range for precise state data.",
        left: "15",
        bottom: "1%",
        textStyle: {
            width: 2 * boxWidth,
            fontSize: 12,
            fontWeight: "normal",
            color: "#828fab",
            fontStyle: "italic",
            overflow: "truncate",
            ellipsis: "...",
        },
        show: isShowNote, // toggle visibility
    };

    const update = {
        ...option,
        title: [baseTitle, secondTitle],
        legend: {
            ...option.legend,
            bottom: isShowNote ? 30 : 10,
        },
        grid: {
            ...option.grid,
            bottom: isShowNote ? 140 : 120,
        },

        dataZoom: option.dataZoom?.map((zoom: any) =>
            zoom.type === "slider"
                ? { ...zoom, bottom: isShowNote ? 70 : 50 }
                : zoom
        ),
    };
    return update;
};

const BasicSensorChart = (props: BasicSensorChartProps) => {
    const jsonList = LocalStorageService.getItem("listOfJSON");
    const { longDateTimeFormat, longTimeFormat } = useDateTimeFormatStore(
        (state) => ({
            longDateTimeFormat: state.longDateTimeFormat,
            longTimeFormat: state.longTimeFormat,
        })
    );
    // ----------------- props -----------------
    const {
        panel: {
            query_time,
            time_range,
            queries,
            attributes,
            refresh_rate,
            uuid: panelId,
            source,
            chart,
            name,
        },
        isVisible,
        visibleRef,
        customEChartStyle,
        customEChartData,
        displayName,
        zoomLevel,
        dashboardId,
        gatewaysConfig,
        allAlerts,
        isFourWiredMotor,
        isActuator = false,
    } = props;

    const panelQuery = queries[0];
    const {
        said,
        device_id: ldsuId,
        gateway_id: gatewayId,
        name: sensorType,
        unit,
        color,
        show_alert,
    } = attributes[0];

    const sensorUnit = (unit || "").toUpperCase() === "NA" ? "" : unit;
    const chartColor = parseColorToHex(color);
    const isCombined = source === ChartSource.MULTIPLE_SENSORS ||
        source === ChartSource.MULTIPLE_ACTUATORS ||
        source === ChartSource.SENSORS_AND_ACTUATORS;

    const chartRef = useRef(null as any);
    const chartInstance: any = chartRef.current?.getEchartsInstance();
    const zr = chartInstance?.getZr();

    // AbortController for cancelling requests
    const abortControllerRef = useRef<AbortController>();

    // Initialize AbortController and cleanup on unmount
    useEffect(() => {
        abortControllerRef.current = new AbortController();

        return () => {
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
        };
    }, []);

    // ----------------- chart -----------------
    const [chartState, setChartState] = useState<ChartState>({
        option: DEFAULT_OPTION,
        hasFetchedConfig: false,
        sensorConfigRes: undefined,
    });
    // ----------------- store chart object state -----------------
    const [isBinaryChart, setIsBinaryChart] = useState<boolean>(false);
    const [accuracy, setAccuracy] = useState<number>(Number(DEFAULT_ACCURACY));

    // ----------------- alertArea -----------------
    const [alertState, setAlertState] = useState<{
        showAlertArea: string;
        isAlertEnabled: boolean;
        ops: any;
        vars_: string[];
    }>({
        showAlertArea: show_alert,
        isAlertEnabled: false,
        ops: [],
        vars_: [],
    });
    const { showAlertArea, isAlertEnabled, ops, vars_ } = alertState;
    // ----------------- refresh rate -----------------
    const {
        refreshRate,
        setRefreshRate,
        editableCustomRate,
        setEditableCustomRate,
    } = useChartRefreshRate(refresh_rate);

    // ----------------- time range -----------------
    const {
        lastTimeRangeSelect,
        setLastTimeRangeSelect,
        getStartDateTime,
        getEndDateTime,
        getTimeGapObject,
    } = useSelectChartTimeRange({
        initial_time_range: time_range,
        initial_query_time: query_time,
        setRefreshRate,
        dashboardId: dashboardId,
        panelId,
    });

    // ----------------- run out of token -----------------
    const [isOutOfPaymentToken, setIsOutOfPaymentToken] = useState(false);

    const { boxWidth } = useChartWidth({
        ref: visibleRef,
        occupiedWidth: 200,
    });

    // ! ----------------- functions -----------------

    const getUpdatedChartConfig = async (selected_time_value: number) => {
        const newLastTimeRangeSelect: any = {};
        if (!APP_CONFIG.APP_DISABLE_MISSING_DATA) {
            const deviceConfigResponse = await getDeviceConfigById(
                gatewayId,
                ldsuId,
                { signal: abortControllerRef.current?.signal }
            );
            if (isHttpSuccess(deviceConfigResponse.status)) {
                newLastTimeRangeSelect.INTVL = get(
                    deviceConfigResponse,
                    "data.INTVL",
                    10
                );
            }
        }

        newLastTimeRangeSelect.showAggregated = shouldShowAggregation(
            selected_time_value,
            lastTimeRangeSelect.INTVL
        );
        newLastTimeRangeSelect.showCandlestickChart = false;
        return newLastTimeRangeSelect;
    };

    const getNewOptions = (
        baseOption: EChartsOption,
        newConfigArr: any = []
    ) => {
        const newOption = cloneDeep(baseOption); // immutable

        if (newOption.title[0]?.subtextStyle?.rich?.s2) {
            newOption.title[0].subtextStyle.rich.s2.color = chartColor;
        }
        newConfigArr.map(({ path, value }: any) => set(newOption, path, value));

        return newOption;
    };

    const fetchNewData = async () => {
        if (!isVisible || !chartState.hasFetchedConfig) return;

        const sensorName = displayName(chartState);

        const convertedQuery = convertLegacyQuery(panelQuery);
        const payload: any = {
            query: convertedQuery,
            chartColor,
            sensorType,
            gapTime: lastTimeRangeSelect.INTVL * 1000,
            time_range: lastTimeRangeSelect.time_range,
            query_time: lastTimeRangeSelect.query_time,
            name: sensorName,
        };

        if (isCombined) {
            payload.isCombinedChart = true;
            payload.gatewayIds = attributes.map((attr: any) => attr.gateway_id);
        }

        let { OBJ: object, APPID: appId } = get(
            chartState,
            "sensorConfigRes",
            {}
        );

        const accuracy: string =
            getAccuracyFromOBJ({
                object,
                jsonList,
                said,
                appId,
                isAggregated: lastTimeRangeSelect.showAggregated,
            }) ?? DEFAULT_ACCURACY;

        setAccuracy(Number(accuracy));

        const sampleRes: any = await getSampleQuery(payload);

        if (Array.isArray(sampleRes.series)) {
            let { series = [], aggregatedSeries = [] } = sampleRes ?? {};

            const { yAxisMin, yAxisMax, dataFormat, isBinary } =
                getYaxisDetails({ object, said, appId });

            // Store isBinary in state for use in rendering
            setIsBinaryChart(isBinary);

            const { showCandlestickChart } = lastTimeRangeSelect;

            updateSeriesPayloadWithNameAndDecimal({
                series,
                accuracy,
                sensorName,
            });

            const { liveData, liveDate, liveTime } = getLiveDataInfo({
                series,
                accuracy,
                timeFormat: longTimeFormat,
            });

            // Ensure we have at least one series for legend display even with no data
            // Added the empty series before adding the alert series
            if (series.length === 0) {
                series.push({
                    name: sensorName,
                    type: "line",
                    data: [],
                    itemStyle: {
                        color: chartColor,
                    },
                });
                // Use this if you want to show a text when there is no data: https://brtchip.atlassian.net/browse/IOTP-7721?focusedCommentId=70966
                // _options.push({
                //     path: "graphic",
                //     value: {
                //         type: "text",
                //         left: "center",
                //         top: "middle",
                //         style: {
                //             text: "No data to display",
                //             fill: chartColor,
                //             fontSize: 16,
                //         },
                //     },
                // });
            }

            // show alert area if sensor alert is enabled
            let thresholdLine: any = [];
            let thresholdArea: any = [];

            const primaryAlertThreshold = vars_[1];
            const secondaryAlertThreshold = vars_[3];

            if (showAlertArea && isAlertEnabled) {
                const thresholds = getChartAreaAlertConfig({
                    vars_,
                    ops,
                    sensorUnit,
                    formatChartValue: createFormattedChartValue(
                        Number(accuracy),
                        false
                    ),
                });

                thresholdLine = thresholds.thresholdLine;
                thresholdArea = thresholds.thresholdArea;

                series.push(
                    chartAlertAreaOptionConfig({
                        thresholdLine,
                        thresholdArea,
                        accuracy,
                    })
                );
            }

            let min: number, max: number;

            // showCandlestickChart -> toggle to candlestick
            if (showCandlestickChart && aggregatedSeries) {
                const minList: [number, string][] = aggregatedSeries[2].data;
                const maxList: [number, string][] = aggregatedSeries[1].data;

                // From candlestick chart, min/max is the last value of min/max list
                min = Number.parseFloat(
                    addDecimalPlace(
                        minList.at(-1)?.[1] ?? "-",
                        accuracy
                    )
                );
                max = Number.parseFloat(
                    addDecimalPlace(
                        maxList.at(-1)?.[1] ?? "-",
                        accuracy
                    )
                );

                if (showAlertArea && isAlertEnabled) {
                    series = aggregatedSeries.map((a: any) => {
                        return {
                            ...a,
                            ...candlestickChartAlertAreaOptionConfig({
                                thresholdLine,
                                thresholdArea,
                                accuracy,
                            }),
                        };
                    });
                } else {
                    series = aggregatedSeries;
                }
            } else {
                const arrayData = series.reduce(
                    (arr: any, chartSegment: any) => [
                        ...arr,
                        ...get(chartSegment, "data", []),
                    ],
                    []
                );

                min = getMinMaxFromArray({
                    array: arrayData,
                    accuracy,
                }).min;
                max = getMinMaxFromArray({
                    array: arrayData,
                    accuracy,
                }).max;
            }

            const _formattedChartValue = createFormattedChartValue(
                Number(accuracy)
            );

            const _options: EChartsOption = [
                {
                    path: "series",
                    value: series,
                },
                {
                    path: "legend.data",
                    value: lastTimeRangeSelect.showCandlestickChart
                        ? ""
                        : [
                            {
                                name: sensorName,
                                itemStyle: { color: chartColor },
                                lineStyle: {
                                    color: chartColor,
                                    type: "solid",
                                },
                            },
                        ],
                },
                {
                    path: "legend.selectedMode",
                    value: lastTimeRangeSelect.showCandlestickChart,
                },
                {
                    path: "title.subtext",
                    value: `{s2|${formatValueWithUnit(
                        liveData === "-" ? "-" : _formattedChartValue(liveData),
                        sensorUnit || ""
                    )}} {s5|    Min/Max} {s3| ${formatValueWithUnit(
                        `${Number.isNaN(min) ? "-" : _formattedChartValue(min)}/${Number.isNaN(max) ? "-" : _formattedChartValue(max)
                        }`,
                        sensorUnit || ""
                    )}}\n{s4| }\n{s1|${liveDate + " " + liveTime}}`,
                },
                {
                    path: "tooltip",
                    value: createTooltipComponentOptions({
                        isCandlestickChart:
                            lastTimeRangeSelect.showCandlestickChart,
                        sensorName,
                        sensorUnit,
                        formatChartValue: _formattedChartValue,
                        longDateTimeFormat,
                    }),
                },
                {
                    path: "xAxis.min",
                    value: Number(getStartDateTime()),
                },
                {
                    path: "xAxis.max",
                    value: Number(getEndDateTime()),
                },
                {
                    path: "yAxis.axisLabel",
                    value: {
                        formatter: (value: any) => {
                            return _formattedChartValue(value);
                        },
                    },
                },
                {
                    path: "title.textStyle.width",
                    value: boxWidth,
                },
                {
                    path: "title.subtextStyle.width",
                    value: boxWidth,
                },
                ...(customEChartData?.({
                    liveData,
                    chartState,
                    liveTime,
                    liveDate,
                    sensorUnit,
                    min,
                    max,
                    accuracy,
                    sensorType,
                    lastTimeRangeSelect,
                    chart,
                }) ?? []),
            ];

            isBinary &&
                _options.push({
                    path: "series[0].step",
                    value: "end",
                });

            isIntegerChart(dataFormat) &&
                _options.push({
                    path: "yAxis.minInterval",
                    value: 1,
                });

            const haveAlertConfig: boolean = !!vars_;
            if (haveAlertConfig) {
                const largestMax = Math.max(
                    Number.isNaN(max) ? -Infinity : max,
                    // single
                    ...(vars_?.[1] ? [Number(primaryAlertThreshold)] : []),
                    // dual
                    ...(vars_?.[3] ? [Number(secondaryAlertThreshold)] : [])
                );
                const smallestMin = Math.min(
                    Number.isNaN(min) ? Infinity : min,
                    // single
                    ...(vars_?.[1] ? [Number(primaryAlertThreshold)] : []),
                    // dual
                    ...(vars_?.[3] ? [Number(secondaryAlertThreshold)] : [])
                );
                _options.push(
                    ...yAxisMinMaxChartOptions({
                        min:
                            smallestMin === undefined
                                ? undefined
                                : (
                                    smallestMin -
                                    (largestMax - smallestMin) * 0.1
                                ),
                        max:
                            largestMax === undefined
                                ? undefined
                                : (
                                    largestMax +
                                    (largestMax - smallestMin) * 0.1
                                ),
                    })
                );
            } else {
                _options.push(
                    ...yAxisMinMaxChartOptions({
                        min: isBinary
                            ? Number(yAxisMin)
                            : getChartMinOrMax({
                                chartSeriesInfo: { yAxisMin, yAxisMax },
                                value: { min, max },
                                type: "min",
                            }),
                        max: isBinary
                            ? Number(yAxisMax)
                            : getChartMinOrMax({
                                chartSeriesInfo: { yAxisMin, yAxisMax },
                                value: { min, max },
                                type: "max",
                            }),
                    })
                );
            }

            let newOption: any = getNewOptions(chartState.option, _options);

            const chartInstance = chartRef.current?.getEchartsInstance();
            const hasZoomConfig = !!chartInstance?.getOption().dataZoom?.[0];

            // Not expect to happen because the dataZoom was set when initializing the chart
            // At this step, it must be available
            if (hasZoomConfig) {
                // Retain previous zoom level after data refresh, set the dataZoom would override the current zoom
                newOption.dataZoom = chartInstance?.getOption().dataZoom;
            } else {
                Object.assign(newOption, cloneDeep(DEFAULT_ZOOM_OPTION));
            }

            setChartState((chartState) => ({
                ...chartState,
                option: newOption,
            }));
        }
        // Error handler
        if (sampleRes.response?.status === FORBIDDEN) {
            if (sampleRes.config.url.includes(PANEL_TOKEN_ZERO_TOKEN)) {
                setIsOutOfPaymentToken(true);
            }
        }
    };

    // ! ----------------- useEffect -----------------
    useEffect(() => {
        if (isOutOfPaymentToken || !isVisible || !chartState.hasFetchedConfig) {
            return;
        }

        if (chartState.hasFetchedConfig && isVisible) {
            fetchNewData();
        }
        if (Number(refreshRate.seconds) === 0) return;

        const { INTVL: sensorReportRate } = lastTimeRangeSelect;

        let getChartRefreshRateInSecs = () => {
            return refreshRate.seconds === "auto"
                ? sensorReportRate
                : refreshRate.seconds;
        };

        const timer = setInterval(() => {
            // Check if component is still active before making API calls
            if (!abortControllerRef.current?.signal.aborted) {
                fetchNewData();
            }
        }, getChartRefreshRateInSecs() * 1000);

        return () => clearInterval(timer);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        boxWidth,
        isVisible,
        chartState.hasFetchedConfig,
        lastTimeRangeSelect.INTVL,
        lastTimeRangeSelect.time_range,
        lastTimeRangeSelect.query_time,
        lastTimeRangeSelect.showAggregated,
        lastTimeRangeSelect.showCandlestickChart,
        alertState.showAlertArea,
        alertState.isAlertEnabled,
        alertState.ops,
        alertState.vars_,
        refreshRate.seconds,
        isOutOfPaymentToken,
    ]);

    const resetZoom = useCallback(() => {
        setChartState((chartState) => ({
            ...chartState,
            option: {
                ...chartState.option,
                dataZoom: DEFAULT_ZOOM_OPTION.dataZoom,
            },
        }));
    }, []);

    const handleTimeSelectionChange = useCallback(
        ({ shouldSetAutoRefresh }: { shouldSetAutoRefresh: boolean }) => {
            // Reset chart zoom when time range changes
            resetZoom();

            if (shouldSetAutoRefresh) {
                setRefreshRate({ seconds: "auto" });
            }
        },
        [setRefreshRate, resetZoom]
    );

    const initChart = async () => {
        if (!isVisible || chartState.hasFetchedConfig) {
            return;
        }

        const sensor = gatewaysConfig?.[gatewayId]?.find(
            ({ UID }: { UID: string }) => UID === ldsuId
        );

        const combinedEntityLabel =
            source === ChartSource.MULTIPLE_ACTUATORS ||
                source === ChartSource.SENSORS_AND_ACTUATORS
                ? "actuators"
                : "sensors";

        const sensorName = isCombined
            ? `Combined data - ${attributes.length} ${combinedEntityLabel}`
            : sensor?.sensor_names[said];

        let panelName = name;
        if (!panelName) {
            panelName = sensor ? sensorName : UnknownDevice;
        }

        const sensorConfigRes = {
            ...sensor,
            ASSOC: sensor?.ASSOC[said],
            CMD: sensor?.CMDS[said],
            MODE: sensor?.MODES[said],
            SAID: sensor?.SAIDS[said],
            sensor_name: sensorName,
            APPID: get(sensor, `APP[${said}].APPID`),
        };
        if (
            isCombined
        ) {
            const sensorIds = new Set(
                attributes.map((attr: any) => attr.device_id)
            );
            const sensors = Object.values(gatewaysConfig)
                .flat()
                .filter((device: any) => sensorIds.has(device.UID));
            const sensorNames = sensors.map(
                (sensor: any) => sensor.sensor_names[said]
            );
            sensorConfigRes.sensorNames = sensorNames;
        }

        let newOption = getNewOptions(chartState.option, [
            ...(source.includes("SENSOR")
                ? sensorChartNoDataStyleOption(
                    sensorType,
                    sensorUnit,
                    source,
                    longDateTimeFormat
                )
                : actuatorChartNoDataStyleOption(
                    sensorType,
                    sensorUnit,
                    sensorConfigRes,
                    longDateTimeFormat
                )),
            ...(customEChartStyle(chartColor) ?? []),
            {
                path: "title.text",
                value: panelName,
            },
            {
                path: "title.textStyle.width",
                value: boxWidth,
            },
            {
                path: "title.subtextStyle.width",
                value: boxWidth,
            },
        ]);

        Object.assign(newOption, cloneDeep(DEFAULT_ZOOM_OPTION));

        // Only update state if the component is still active
        if (!abortControllerRef.current?.signal.aborted) {
            sensor && setIsOutOfPaymentToken(false);

            const sensorReportRate = get(sensorConfigRes, "INTVL", 10);
            setLastTimeRangeSelect({
                ...lastTimeRangeSelect,
                showAggregated: shouldShowAggregation(
                    convertPrometheusTimeToSeconds(
                        lastTimeRangeSelect.time_range
                    ),
                    sensorReportRate
                ),
                INTVL: sensorReportRate,
            });

            setChartState((chartState) => ({
                ...chartState,
                hasFetchedConfig: true,
                option: newOption,
                sensorConfigRes,
            }));
        }
    };

    useEffect(() => {
        initChart();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [chartState, isVisible, isOutOfPaymentToken, gatewaysConfig, boxWidth]);

    useEffect(() => {
        const checkIfAlertEnabled = async () => {
            if (
                !abortControllerRef.current ||
                abortControllerRef.current.signal.aborted
            )
                return;

            const _alertRes: Partial<Alert> =
                allAlerts?.find((alert) => {
                    return (
                        alert.gateway_id === gatewayId &&
                        alert.device_id === ldsuId &&
                        alert.said === said
                    );
                }) ?? {};

            if (!isEmpty(_alertRes)) {
                const { enabled, ops, vars_ } = _alertRes;
                if (enabled && !abortControllerRef.current.signal.aborted) {
                    setAlertState({
                        ...alertState,
                        isAlertEnabled: true,
                        ops,
                        vars_: vars_ ?? [],
                    });
                }
            }
        };
        checkIfAlertEnabled();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [allAlerts]);

    // Delta min max and change split for I/O Chart
    const changeAxisYMaxMin = (
        option: EChartsOption,
        deltaPercent: number = 0.2
    ) => {
        const newOption = cloneDeep(option);
        const { yAxis } = newOption;
        const min = Number(yAxis.min),
            max = Number(yAxis.max);


        // Will only use interval
        delete yAxis.splitNumber;

        // Case 1: Four wired motor controller - exact values [-1, 0, 1]
        if (isFourWiredMotor) {
            yAxis.min = -1;
            yAxis.max = 1;
            // Use interval of 1 instead of splitNumber to ensure exact values of -1, 0, 1
            yAxis.interval = 1;
            return newOption;
        }

        // Case 2: Binary data (aggregated or not) - exact values [0, 1]
        if (isBinaryChart) {
            yAxis.min = 0;
            yAxis.max = 1;
            // Use interval of 1 instead of splitNumber to ensure exact values of 0, 1
            yAxis.interval = 1;
            return newOption;
        }

        // Case 3: Normal chart with proper padding and split lines
        const roundedDecimal = accuracy;

        // Handle case where min and max are equal
        if (max === min) {
            // Create a reasonable range around the single value
            let buffer = Math.pow(10, -roundedDecimal) * 2;

            const paddedMin = Number((min - buffer).toFixed(roundedDecimal));
            const paddedMax = Number((min + buffer).toFixed(roundedDecimal));

            // Make sure we have at least 5 distinct values
            const range = paddedMax - paddedMin;
            // Calculate an interval that will create exactly 4 intervals (5 values)
            let interval = Number((range / 4).toFixed(roundedDecimal));

            yAxis.min = paddedMin;
            yAxis.max = paddedMax;

            if (interval === 0) {
                // To prevent empty yAxis
                interval = Math.pow(10, -roundedDecimal);
            }
            yAxis.interval = interval;

            return newOption;
        }

        // Normal case with different min/max values
        // Calculate padding based on the range
        const range = max - min;

        // Adjust delta padding based on range characteristics
        let adjustedDeltaPercent = deltaPercent;

        // For small ranges, increase padding
        if (range < 1) {
            adjustedDeltaPercent = 0.5;
        }

        // Special handling for zero or near-zero values
        if (Math.abs(min) < 0.001 || Math.abs(max) < 0.001) {
            adjustedDeltaPercent = 0.3;
        }

        // Calculate padded min/max with adjusted padding
        const paddedMin = min - Math.abs(range * adjustedDeltaPercent);
        const paddedMax = max + Math.abs(range * adjustedDeltaPercent);

        // Format with appropriate decimal places
        const formattedMin = Number(paddedMin.toFixed(roundedDecimal));
        const formattedMax = Number(paddedMax.toFixed(roundedDecimal));

        // Calculate number of intervals based on desired line count (5-8 lines)
        const targetIntervals = 6; // Target 6 intervals (7 lines)

        // Calculate ideal step size
        let rawStep = (formattedMax - formattedMin) / targetIntervals;

        // Round the step to a clean number at the appropriate decimal place
        // This helps prevent duplicate values due to floating point rounding
        const decimalPower = Math.pow(10, roundedDecimal);
        const cleanStep = Math.ceil(rawStep * decimalPower) / decimalPower;

        // Ensure step isn't too small
        const minStep = Math.pow(10, -roundedDecimal);
        let finalStep = Math.max(cleanStep, minStep);

        // Recalculate the max value based on min + N*interval to ensure even distribution
        // This prevents having partial intervals that might lead to duplicate values
        const intervals = Math.floor((formattedMax - formattedMin) / finalStep);
        const adjustedMax = formattedMin + intervals * finalStep;

        // Add one more interval if we ended up with fewer than target
        const finalMax =
            intervals < targetIntervals - 1
                ? adjustedMax + finalStep
                : adjustedMax;

        yAxis.min = formattedMin;

        // If adjusted max is less than the original max, add one more interval
        yAxis.max = Number(finalMax > max ? finalMax : finalMax + finalStep).toFixed(roundedDecimal);

        if (finalStep === 0) {
            // To prevent empty yAxis
            finalStep = Math.pow(10, -roundedDecimal);
        }

        yAxis.interval = finalStep;

        return newOption;
    };

    const onClickAggregate = () => {
        setLastTimeRangeSelect({
            ...lastTimeRangeSelect,
            showCandlestickChart: !lastTimeRangeSelect.showCandlestickChart,
        });
    };

    useEffect(() => {
        if (!zr) {
            return;
        }

        const handleRestore = (a: any) => {
            // Only runs when user clicks the Restore button and component is still active
            if (
                a?.target?.__title === "Restore" &&
                !abortControllerRef.current?.signal.aborted
            ) {
                resetZoom();
                fetchNewData();
            }
        };

        zr.on("click", handleRestore);

        return () => {
            if (zr) {
                zr.off("click", handleRestore);
            }
        };
    }); // Must not add dependencies here, because we need the latest variable values from fetchNewData function

    const chartOptions = useMemo(() => {
        const base = changeAxisYMaxMin(chartState.option);

        const updatedOption = addNoteToChart(
            isActuator,
            isBinaryChart,
            lastTimeRangeSelect.showAggregated,
            base,
            boxWidth
        );

        return {
            ...updatedOption,
            xAxis: getCommonXAxis(base),
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [chartState.option]);

    return (
        <WidgetBox
            ref={visibleRef}
            className={
                zoomLevel <= 0.5
                    ? "widget-box w-100 h-30vh"
                    : "widget-box w-100"
            }
        >
            <div className="widget">
                <ChartTimeSelectionDropdown
                    lastTimeRangeSelect={lastTimeRangeSelect}
                    setLastTimeRangeSelect={setLastTimeRangeSelect}
                    getStartDateTime={getStartDateTime}
                    getEndDateTime={getEndDateTime}
                    dashboardId={dashboardId}
                    panelId={panelId}
                    getTimeGapObject={getTimeGapObject}
                    getUpdatedChartConfig={getUpdatedChartConfig}
                    onChange={handleTimeSelectionChange}
                />
                {lastTimeRangeSelect.query_time ? (
                    <CustomRangeDetails
                        lastTimeRangeSelect={lastTimeRangeSelect}
                    />
                ) : (
                    <></>
                )}

                {isCombined && (
                    <SensorListTooltip
                        sensorNames={chartState.sensorConfigRes?.sensorNames}
                    />
                )}

                <div
                    className="widget-tool"
                    style={{
                        bottom:
                            (isBinaryChart || isActuator) &&
                                lastTimeRangeSelect.showAggregated
                                ? "30px"
                                : "12px",
                    }}
                >
                    {source !== ChartSource.MULTIPLE_SENSORS &&
                        source !== ChartSource.MULTIPLE_ACTUATORS &&
                        source !== ChartSource.SENSORS_AND_ACTUATORS && (
                            <Avatar
                                alt="aggregate-icon"
                                src={
                                    lastTimeRangeSelect.showCandlestickChart
                                        ? LineChartIcon
                                        : CandlestickChartIcon
                                }
                                onClick={onClickAggregate}
                                onKeyDown={onClickAggregate}
                                tabIndex={0}
                                className={
                                    lastTimeRangeSelect.showAggregated
                                        ? "aggregate-icon d-block mr-3"
                                        : "aggregate-icon d-none"
                                }
                                sx={{
                                    width: 25,
                                    height: 25,
                                    cursor: "pointer",
                                }}
                            />
                        )}

                    <RefreshRateDropdown
                        refreshRate={refreshRate}
                        setRefreshRate={setRefreshRate}
                        editableCustomRate={editableCustomRate}
                        setEditableCustomRate={setEditableCustomRate}
                        dashboardId={dashboardId}
                        panelId={panelId}
                        lastTimeRangeSelect={lastTimeRangeSelect}
                    />
                </div>

                <ContentWrapper
                    isLoading={
                        isOutOfPaymentToken
                            ? false
                            : !chartState.hasFetchedConfig
                    }
                >
                    <ReactECharts
                        ref={chartRef}
                        className="chart-box"
                        theme={"dark"}
                        option={chartOptions}
                        opts={{ locale: navigator.language }}
                        lazyUpdate={true}
                        notMerge={true}
                    />
                </ContentWrapper>
            </div>
        </WidgetBox>
    );
};

export default BasicSensorChart;
